import React, { useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

interface ChatInputProps {
  onSendMessage: (text: string) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  placeholder = "Type your message...",
}) => {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const triggerSubmit = async () => {
    if (!message.trim() || disabled || sending) return;

    setSending(true);
    try {
      await onSendMessage(message.trim());
      setMessage("");
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await triggerSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      triggerSubmit();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
      }}
    >
      <TextField
        data-testid="chat-input-field"
        fullWidth
        multiline
        maxRows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled || sending}
        placeholder={placeholder}
        sx={{ flex: 1 }}
        InputProps={{
          sx: { backgroundColor: "background.paper" },
        }}
      />
      <Tooltip title={disabled ? "Cannot send message" : "Send message"}>
        <span>
          <IconButton
            type="submit"
            data-testid="chat-send-button"
            disabled={!message.trim() || disabled || sending}
            color="primary"
          >
            {sending ? (
              <CircularProgress size={24} />
            ) : (
              <SendIcon fontSize="medium" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
